import { useMemo, useState, useCallback, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  useTemplate,
  useCreateTemplate,
  useUpdateTemplate,
  useUploadTemplateThumbnail,
  useUploadTemplateVideo,
  useAddTemplateFrameImages,
  useDeleteFrameImage,
  useDeleteTemplateThumbnail,
  useDeleteTemplateVideo,
  useCategories,
  apiClient,
} from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { X, Upload, ImagePlus, Video, Loader2 } from 'lucide-react'
import { I18nFieldsSection, I18nPromptFieldsSection } from '../components/I18nFieldsSection'
import { toast } from 'sonner'
import { VIDEO_RATIO_OPTIONS, VIDEO_RESOLUTION_OPTIONS } from '@/lib/constants'
import i18n from '@/i18n'

// 어드민 UI는 한국어 고정 - 비율/해상도 라벨도 ko 리소스에서 직접 조회한다
const tKo = i18n.getFixedT('ko')

export function TemplateForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEdit = !!id

  const { data: template } = useTemplate(id!)
  const { data: categories } = useCategories()
  const createMutation = useCreateTemplate()
  const updateMutation = useUpdateTemplate(id!)
  const uploadThumbnailMutation = useUploadTemplateThumbnail(id!)
  const uploadVideoMutation = useUploadTemplateVideo(id!)
  const addFrameImagesMutation = useAddTemplateFrameImages()
  const deleteFrameImageMutation = useDeleteFrameImage()
  const deleteThumbnailMutation = useDeleteTemplateThumbnail(id!)
  const deleteVideoMutation = useDeleteTemplateVideo(id!)

  // 생성 모드에서 임시 보관할 프레임 이미지 (pending upload)
  const [pendingFrameImages, setPendingFrameImages] = useState<
    Array<{
      id: string
      file: File
      preview: string // Data URL
      role: 'first_frame' | 'last_frame'
    }>
  >([])

  const [isDraggingThumbnail, setIsDraggingThumbnail] = useState(false)
  const [isDraggingFrame, setIsDraggingFrame] = useState<'first_frame' | 'last_frame' | null>(null)
  const [isDraggingVideo, setIsDraggingVideo] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  type TemplateFormData = {
    categoryId: string
    name: string
    description: string
    nameI18n: Record<string, string>
    descriptionI18n: Record<string, string>
    textPrompt: string
    textPromptI18n: Record<string, string>
    promptSuffix: string
    ratio: string
    resolution: string
    duration: number
    listOrder: number
    enabled: boolean
  }

  const baseFormData = useMemo<TemplateFormData>(
    () =>
      template
        ? {
            categoryId: template.categoryId || '',
            name: template.name,
            description: template.description || '',
            nameI18n: template.nameI18n ?? {},
            descriptionI18n: template.descriptionI18n ?? {},
            textPrompt: template.textPrompt,
            textPromptI18n: template.textPromptI18n ?? {},
            promptSuffix: template.promptSuffix || '',
            ratio: template.ratio,
            resolution: template.resolution || '768P',
            duration: template.duration || 5,
            listOrder: template.listOrder,
            enabled: template.enabled,
          }
        : {
            categoryId: '',
            name: '',
            description: '',
            nameI18n: {},
            descriptionI18n: {},
            textPrompt: '',
            textPromptI18n: {},
            promptSuffix: '',
            ratio: '16:9',
            resolution: '768P',
            duration: 5,
            listOrder: 0,
            enabled: true,
          },
    [template],
  )
  const [draftFormData, setDraftFormData] = useState<TemplateFormData | null>(null)
  const formData = draftFormData ?? baseFormData

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null)
  const [thumbnailPreviewOverride, setThumbnailPreviewOverride] = useState<string | null>(null)
  const [thumbnailDeleted, setThumbnailDeleted] = useState(false)
  const baseThumbnailPreview =
    template?.thumbnailUrl ? `${template.thumbnailUrl}?v=${new Date(template.updatedAt).getTime()}` : ''
  const thumbnailPreview = thumbnailPreviewOverride ?? baseThumbnailPreview
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [videoPreviewOverride, setVideoPreviewOverride] = useState<string | null>(null)
  const [videoDeleted, setVideoDeleted] = useState(false)
  const baseVideoPreview =
    template?.videoUrl ? `${template.videoUrl}?v=${new Date(template.updatedAt).getTime()}` : ''
  const videoPreview = videoPreviewOverride ?? baseVideoPreview

  useEffect(() => {
    return () => {
      if (videoPreviewOverride?.startsWith('blob:')) {
        URL.revokeObjectURL(videoPreviewOverride)
      }
    }
  }, [videoPreviewOverride])

  const updateField = <K extends keyof TemplateFormData>(key: K, value: TemplateFormData[K]) => {
    setDraftFormData((prev) => ({ ...(prev ?? baseFormData), [key]: value }))
  }

  const updateI18nField = (field: 'nameI18n' | 'descriptionI18n', lang: string, text: string) => {
    setDraftFormData((prev) => {
      const base = prev ?? baseFormData
      return { ...base, [field]: { ...base[field], [lang]: text } }
    })
  }

  const updateTextPromptI18n = (lang: string, text: string) => {
    setDraftFormData((prev) => {
      const base = prev ?? baseFormData
      return { ...base, textPromptI18n: { ...base.textPromptI18n, [lang]: text } }
    })
  }

  const processThumbnailFile = (file: File) => {
    setThumbnailFile(file)
    setThumbnailDeleted(false)
    const reader = new FileReader()
    reader.onload = () => setThumbnailPreviewOverride(reader.result as string)
    reader.readAsDataURL(file)
  }

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    processThumbnailFile(file)
  }

  const processVideoFile = (file: File) => {
    setVideoFile(file)
    setVideoDeleted(false)
    const objectUrl = URL.createObjectURL(file)
    setVideoPreviewOverride((prev) => {
      if (prev?.startsWith('blob:')) {
        URL.revokeObjectURL(prev)
      }
      return objectUrl
    })
  }

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    processVideoFile(file)
  }

  const handleVideoDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingVideo(false)

    const file = e.dataTransfer.files?.[0]
    if (!file) return

    const isVideoFile =
      file.type.startsWith('video/') || /\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(file.name)
    if (!isVideoFile) return

    processVideoFile(file)
  }

  const processFrameImageFile = async (role: 'first_frame' | 'last_frame', file: File) => {
    // 수정 모드: 즉시 API 호출
    if (isEdit && id) {
      const existingFrame = template?.frameImages?.find((f) => f.role === role)
      if (existingFrame) {
        toast.error(`이미 ${role === 'first_frame' ? '첫 프레임' : '마지막 프레임'} 이미지가 존재합니다. 기존 이미지를 삭제 후 추가해주세요.`)
        return
      }

      try {
        const dataUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.readAsDataURL(file)
        })

        await addFrameImagesMutation.mutateAsync({
          templateId: id,
          images: [{ dataUrl, role }],
        })
        toast.success('프레임 이미지가 추가되었습니다')
      } catch (error: unknown) {
        toast.error(`이미지 업로드 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`)
      }
      return
    }

    // 생성 모드: state에 임시 보관
    const existingPending = pendingFrameImages.find((f) => f.role === role)
    if (existingPending) {
      toast.error(`이미 ${role === 'first_frame' ? '첫 프레임' : '마지막 프레임'} 이미지가 존재합니다. 기존 이미지를 삭제 후 추가해주세요.`)
      return
    }

    const preview = await new Promise<string>((resolve) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.readAsDataURL(file)
    })

    setPendingFrameImages((prev) => [
      ...prev,
      {
        id: `pending-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        file,
        preview,
        role,
      },
    ])
  }

  const handleFrameImageAdd = async (role: 'first_frame' | 'last_frame', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    await processFrameImageFile(role, file)
    e.target.value = ''
  }

  // Drag & drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleThumbnailDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDraggingThumbnail(false)
      const file = e.dataTransfer.files?.[0]
      if (!file || !file.type.startsWith('image/')) return
      processThumbnailFile(file)
    },
    [],
  )

  const handleFrameImageDrop = useCallback(
    (role: 'first_frame' | 'last_frame', e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDraggingFrame(null)
      const file = e.dataTransfer.files?.[0]
      if (!file || !file.type.startsWith('image/')) return
      processFrameImageFile(role, file)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEdit, id, template, pendingFrameImages],
  )

  const handleDeleteFrameImage = async (frameImageId: string) => {
    if (!confirm('이 프레임 이미지를 삭제하시겠습니까?')) return

    try {
      await deleteFrameImageMutation.mutateAsync({ templateId: id!, frameImageId })
      toast.success('프레임 이미지가 삭제되었습니다')
    } catch (error: unknown) {
      toast.error(`삭제 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`)
    }
  }

  // 생성 모드에서 pending 프레임 이미지 삭제
  const handleDeletePendingFrameImage = (pendingId: string) => {
    setPendingFrameImages((prev) => prev.filter((f) => f.id !== pendingId))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    const loadingToastId = toast.loading(isEdit ? '템플릿 수정 중...' : '템플릿 생성 중...')

    try {
      let templateId = id

      // 1. 템플릿 생성 또는 수정
      const payload = formData
      if (isEdit) {
        await updateMutation.mutateAsync(payload)
      } else {
        const created = await createMutation.mutateAsync(payload)
        templateId = created.id
      }

      // 2. 썸네일 처리
      if (thumbnailFile && templateId) {
        // 새 썸네일 업로드
        const dataUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.readAsDataURL(thumbnailFile)
        })

        if (isEdit) {
          await uploadThumbnailMutation.mutateAsync(dataUrl)
        } else {
          await apiClient.post(`/admin/templates/${templateId}/thumbnail`, { dataUrl })
        }
      } else if (isEdit && thumbnailDeleted) {
        // 기존 썸네일 삭제
        await deleteThumbnailMutation.mutateAsync()
      }

      // 3. 템플릿 동영상 처리 (선택)
      if (videoFile && templateId) {
        if (isEdit) {
          await uploadVideoMutation.mutateAsync(videoFile)
        } else {
          const formData = new FormData()
          formData.append('file', videoFile)
          await apiClient.post(`/admin/templates/${templateId}/video`, formData)
        }
      } else if (isEdit && videoDeleted && template?.videoUrl) {
        await deleteVideoMutation.mutateAsync()
      }

      // 4. 프레임 이미지 업로드 (생성 모드에서만)
      if (!isEdit && pendingFrameImages.length > 0 && templateId) {
        const images = pendingFrameImages.map((f) => ({
          dataUrl: f.preview,
          role: f.role,
        }))
        await addFrameImagesMutation.mutateAsync({ templateId, images })
      }

      toast.success(isEdit ? '템플릿이 수정되었습니다' : '템플릿이 생성되었습니다', {
        id: loadingToastId,
      })

      if (isEdit) {
        // refetchQueries는 refetch 완료 시 resolve되는 Promise를 반환하므로
        // navigate 전에 캐시가 최신 상태임을 보장
        await queryClient.refetchQueries({ queryKey: ['admin', 'templates', id] })
        navigate(`/admin/templates/${id}/view`)
      } else {
        // 생성 모드: 썸네일/프레임 업로드가 raw fetch로 처리되어 캐시 무효화 누락됨
        await queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] })
        navigate('/admin/templates')
      }
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : '저장 중 오류가 발생했습니다', {
        id: loadingToastId,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Helper: get existing frame image for a role (edit mode)
  const getExistingFrame = (role: 'first_frame' | 'last_frame') => {
    return template?.frameImages?.find((f) => f.role === role) ?? null
  }

  // Helper: get pending frame image for a role (create mode)
  const getPendingFrame = (role: 'first_frame' | 'last_frame') => {
    return pendingFrameImages.find((f) => f.role === role) ?? null
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-heading tracking-tight">
          {isEdit ? '템플릿 수정' : '새 템플릿'}
        </h1>
        <p className="text-caption mt-2 leading-relaxed">
          {isEdit ? '템플릿 정보를 수정합니다' : '새로운 템플릿을 생성합니다'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg border">
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name">이름 *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            required
            placeholder="템플릿 이름"
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">설명</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="템플릿 설명"
            rows={2}
          />
        </div>

        {/* Translations */}
        <I18nFieldsSection
          value={{ nameI18n: formData.nameI18n, descriptionI18n: formData.descriptionI18n }}
          onChange={updateI18nField}
          withDescription
        />

        {/* Text Prompt */}
        <div className="space-y-2">
          <Label htmlFor="textPrompt">프롬프트 *</Label>
          <Textarea
            id="textPrompt"
            value={formData.textPrompt}
            onChange={(e) => updateField('textPrompt', e.target.value)}
            required
            placeholder="비디오 생성 프롬프트"
            rows={4}
          />
        </div>

        {/* Prompt Translations */}
        <I18nPromptFieldsSection value={formData.textPromptI18n} onChange={updateTextPromptI18n} />

        {/* Category */}
        <div className="space-y-2">
          <Label htmlFor="categoryId">카테고리</Label>
          <select
            id="categoryId"
            value={formData.categoryId}
            onChange={(e) => updateField('categoryId', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">선택 안함</option>
            {categories?.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        {/* Prompt Suffix */}
        <div className="space-y-2">
          <Label htmlFor="promptSuffix">프롬프트 접미사</Label>
          <Textarea
            id="promptSuffix"
            value={formData.promptSuffix}
            onChange={(e) => updateField('promptSuffix', e.target.value)}
            placeholder="프롬프트에 자동으로 추가되는 내부 접미사"
            rows={2}
          />
          <p className="text-sm text-gray-500">
            프롬프트에 자동으로 추가되는 내부 접미사입니다.
          </p>
        </div>

        {/* Ratio */}
        <div className="space-y-2">
          <Label htmlFor="ratio">비율</Label>
          <select
            id="ratio"
            value={formData.ratio}
            onChange={(e) => updateField('ratio', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            {VIDEO_RATIO_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.id} ({tKo(option.labelKey)})
              </option>
            ))}
          </select>
        </div>

        {/* Resolution */}
        <div className="space-y-2">
          <Label htmlFor="resolution">해상도</Label>
          <select
            id="resolution"
            value={formData.resolution}
            onChange={(e) => updateField('resolution', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            {VIDEO_RESOLUTION_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label} - {tKo(option.descriptionKey)}
              </option>
            ))}
          </select>
        </div>

        {/* Duration */}
        <div className="space-y-2">
          <Label htmlFor="duration">길이 (초)</Label>
          <Input
            id="duration"
            type="number"
            min={4}
            max={15}
            value={formData.duration}
            onChange={(e) => updateField('duration', Number.parseInt(e.target.value, 10) || 5)}
          />
        </div>

        {/* List Order */}
        <div className="space-y-2">
          <Label htmlFor="listOrder">정렬 순서</Label>
          <Input
            id="listOrder"
            type="number"
            value={formData.listOrder}
            onChange={(e) => updateField('listOrder', Number.parseInt(e.target.value, 10) || 0)}
          />
        </div>

        {/* Enabled */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="enabled"
            checked={formData.enabled}
            onChange={(e) => updateField('enabled', e.target.checked)}
            className="w-4 h-4 rounded border-gray-300"
          />
          <Label htmlFor="enabled">활성화</Label>
        </div>

        {/* Thumbnail */}
        <div className="space-y-2">
          <Label>썸네일</Label>
          <div
            className={`relative w-[21.6rem] h-[21.6rem] rounded-lg overflow-hidden ${
              thumbnailPreview
                ? 'group'
                : `border-2 border-dashed transition-colors ${
                    isDraggingThumbnail
                      ? 'border-primary bg-primary/10'
                      : 'border-slate-300 hover:border-primary/50 bg-slate-50 hover:bg-slate-100 cursor-pointer'
                  }`
            }`}
            onClick={!thumbnailPreview ? () => document.getElementById('thumbnail')?.click() : undefined}
            onDrop={!thumbnailPreview ? handleThumbnailDrop : undefined}
            onDragOver={!thumbnailPreview ? handleDragOver : undefined}
            onDragEnter={!thumbnailPreview ? (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingThumbnail(true) } : undefined}
            onDragLeave={!thumbnailPreview ? (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingThumbnail(false) } : undefined}
          >
            {thumbnailPreview ? (
              <>
                <img
                  src={thumbnailPreview}
                  alt="썸네일 미리보기"
                  className="w-full h-full object-contain bg-slate-100"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        document.getElementById('thumbnail')?.click()
                      }}
                      className="p-2 bg-white rounded-full hover:bg-slate-100"
                      title="썸네일 변경"
                    >
                      <ImagePlus className="w-4 h-4 text-slate-700" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setThumbnailFile(null)
                        setThumbnailPreviewOverride('')
                        setThumbnailDeleted(true)
                      }}
                      className="p-2 bg-white rounded-full hover:bg-slate-100"
                      title="썸네일 삭제"
                    >
                      <X className="w-4 h-4 text-slate-700" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                <ImagePlus className="w-8 h-8 text-slate-400" />
                <span className="text-sm text-slate-500">썸네일 업로드</span>
              </div>
            )}
          </div>
          <input
            type="file"
            id="thumbnail"
            accept="image/*"
            onChange={handleThumbnailChange}
            className="hidden"
          />
        </div>

        {/* Video */}
        <div className="space-y-2">
          <Label>동영상 (선택)</Label>
          <div
            className="w-full max-w-md"
            onDrop={handleVideoDrop}
            onDragOver={handleDragOver}
            onDragEnter={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setIsDraggingVideo(true)
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setIsDraggingVideo(false)
            }}
          >
            {videoPreview ? (
              <div
                className={`relative group rounded-lg overflow-hidden border bg-slate-100 transition-colors flex items-center justify-center ${
                  isDraggingVideo ? 'ring-2 ring-primary/40 border-primary' : ''
                }`}
              >
                <video
                  src={videoPreview}
                  poster={videoPreviewOverride ? undefined : thumbnailPreview || undefined}
                  controls
                  preload="metadata"
                  className="w-auto max-w-full h-auto max-h-[569px]"
                />
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                  <button
                    type="button"
                    onClick={() => document.getElementById('template-video')?.click()}
                    className="p-2 bg-white rounded-full hover:bg-slate-100"
                    title="동영상 변경"
                  >
                    <Upload className="w-4 h-4 text-slate-700" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setVideoFile(null)
                      setVideoPreviewOverride((prev) => {
                        if (prev?.startsWith('blob:')) {
                          URL.revokeObjectURL(prev)
                        }
                        return ''
                      })
                      setVideoDeleted(true)
                    }}
                    className="p-2 bg-white rounded-full hover:bg-slate-100"
                    title="동영상 삭제"
                  >
                    <X className="w-4 h-4 text-slate-700" />
                  </button>
                </div>
              </div>
            ) : (
              <label
                htmlFor="template-video"
                className={`w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors flex flex-col items-center justify-center gap-2 ${
                  isDraggingVideo
                    ? 'border-primary bg-primary/10'
                    : 'border-slate-300 hover:border-primary/50 bg-slate-50 hover:bg-slate-100'
                }`}
              >
                <Video className="w-7 h-7 text-slate-400" />
                <span className="text-sm text-slate-500">동영상 업로드</span>
              </label>
            )}
          </div>
          <input
            type="file"
            id="template-video"
            accept="video/*,.mp4,.mov,.webm,.mkv,.avi,.m4v"
            onChange={handleVideoChange}
            className="hidden"
          />
          <p className="text-sm text-gray-500">
            동영상은 선택사항입니다. 비호환 포맷은 업로드 시 자동으로 브라우저 호환 포맷(MP4)으로 변환됩니다.
          </p>
        </div>

        {/* Frame Images */}
        <div className="space-y-4 pt-6 border-t">
          <div>
            <Label>프레임 이미지</Label>
            <p className="text-sm text-gray-600 mt-1">
              첫 프레임과 마지막 프레임 이미지를 각각 최대 1개씩 추가할 수 있습니다.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* First Frame Slot */}
            {(['first_frame', 'last_frame'] as const).map((role) => {
              const roleLabel = role === 'first_frame' ? '첫 프레임' : '마지막 프레임'
              const existingFrame = isEdit ? getExistingFrame(role) : null
              const pendingFrame = !isEdit ? getPendingFrame(role) : null
              const hasImage = !!existingFrame || !!pendingFrame
              const inputId = `frameImage-${role}`

              return (
                <div key={role} className="space-y-2">
                  <label className="text-sm font-medium text-gray-600">{roleLabel}</label>
                  {hasImage ? (
                    <div className="relative group bg-gray-50 rounded-lg border">
                      <img
                        src={
                          existingFrame
                            ? `${existingFrame.thumbnailUrl}?v=${new Date(existingFrame.createdAt).getTime()}`
                            : pendingFrame!.preview
                        }
                        alt={roleLabel}
                        className="w-full h-32 object-contain rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          existingFrame
                            ? handleDeleteFrameImage(existingFrame.id)
                            : handleDeletePendingFrameImage(pendingFrame!.id)
                        }
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label htmlFor={inputId}>
                      <div
                        className={`w-full h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-1 cursor-pointer transition-colors ${
                          isDraggingFrame === role
                            ? 'border-primary bg-primary/10'
                            : 'border-gray-300 hover:border-primary/50 hover:bg-slate-50'
                        }`}
                        onDrop={(e) => handleFrameImageDrop(role, e)}
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingFrame(role) }}
                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingFrame(null) }}
                      >
                        <Upload className="w-5 h-5 text-gray-400" />
                        <span className="text-xs text-gray-500">이미지 추가</span>
                      </div>
                      <input
                        type="file"
                        id={inputId}
                        accept="image/*"
                        onChange={(e) => handleFrameImageAdd(role, e)}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Buttons */}
        <div className="flex items-center space-x-4 pt-4">
          <Button
            type="submit"
            disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                저장 중...
              </>
            ) : (
              isEdit ? '수정' : '생성'
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => navigate(-1)}
          >
            취소
          </Button>
        </div>
      </form>
    </div>
  )
}
